import clsx from "clsx";
import { ReactNode, useState } from "react";
import { Theme } from "react-migration/lib/theme/Theme";
import { Tooltip } from "../Tooltip";
import { Alignment, Column, IRow, TableSize } from "./Table";
import { Checkbox, CheckboxState } from "react-migration/components/DeprecatedCheckbox";

const ItemWithTooltip = ({
  trigger,
  content,
  cellClassName,
  width,
}: {
  trigger: ReactNode;
  content?: ReactNode;
  cellClassName: string;
  width?: string;
}) => {
  return (
    <Tooltip.Wrapper>
      <Tooltip.Trigger>
        <div className={cellClassName} style={{ width }}>
          {trigger}
        </div>
      </Tooltip.Trigger>
      <Tooltip.Content align="center" side="bottom" theme={Theme.Dark} size="sm">
        {content}
      </Tooltip.Content>
    </Tooltip.Wrapper>
  );
};

interface TableRowsProps<Row extends IRow> {
  primaryKey: keyof Row;
  rows: Row[];
  columns: Column<Row>[];
  selection?: {
    isRowSelected: (row: Row) => boolean;
    selectRow: (row: Row) => void;
    selectWithRowClick?: boolean;
    disabled?: (row: Row) => boolean;
  };
  size: TableSize;
  cellClassName?: (row: Row, column: Column<Row>) => string | undefined;
  rowClassName?: (row: Row) => string | undefined;
  onRowHover?: (row?: Row) => void;
  highlightRowOnHover?: boolean;
}

export function TableRows<Row extends IRow>({
  rows,
  columns,
  selection,
  primaryKey,
  size,
  rowClassName,
  cellClassName: cellClassNameExt,
  onRowHover,
  highlightRowOnHover = true,
}: TableRowsProps<Row>) {
  const [truncatedElementIds, setTruncatedElementIds] = useState<string[]>([]);

  const selectRow = (row: Row) => () => {
    if (!selection?.disabled?.(row)) {
      selection?.selectRow(row);
    }
  };

  const getCellClass = (align: Alignment, grow?: boolean, isRowDisabled?: boolean) =>
    clsx(
      isRowDisabled ? "text-text-base-primary-disabled" : "text-text-base-secondary",
      "font-normal text-sm leading-5",
      "whitespace-nowrap text-ellipsis overflow-hidden w-full",
      {
        "py-2 px-3": size === "small",
        "py-4 px-6": size !== "small",
        "text-right": align === "right",
        "text-left": align === "left",
        "text-center": align === "center",
        "whitespace-nowrap w-px": !grow,
      }
    );

  const verifyOverflowingElement = (element: HTMLTableCellElement | null, colId: string) => {
    if (!element) {
      return;
    }
    const isEllipsisActive = element.offsetHeight < element.scrollHeight || element.offsetWidth < element.scrollWidth;

    if (isEllipsisActive && !truncatedElementIds.includes(colId)) {
      setTruncatedElementIds((prev) => [...prev, colId]);
    }
  };

  return (
    <tbody className="bg-white" data-testid="table-body">
      {rows.map((row, rowIndex) => {
        const isRowDisabled = selection?.disabled?.(row);
        const rowIdentifier = row[primaryKey] ?? `fallback-id-${rowIndex}`;
        return (
          <tr
            key={`row-${rowIdentifier}-${rowIndex}`}
            data-testid={`row-${rowIdentifier}`}
            onClick={selection?.selectWithRowClick ? selectRow(row) : undefined}
            onMouseEnter={() => onRowHover?.(row)}
            onMouseLeave={() => onRowHover?.(undefined)}
            className={clsx(
              "border-x-0 border-t-0 border-b last:border-b-0 border-solid border-border-action",
              {
                "hover:bg-background-surface-neutral": highlightRowOnHover,
              },
              {
                "cursor-pointer": selection?.selectWithRowClick,
                "pointer-events-none bg-background-surface-neutral/50": isRowDisabled,
              },
              rowClassName?.(row)
            )}
          >
            {selection && (
              <td className="w-11 pt-1 pl-4" data-testid={`row-${rowIdentifier}-checkbox-cell`}>
                <Checkbox
                  value={!isRowDisabled && selection.isRowSelected(row) ? CheckboxState.CHECKED : CheckboxState.EMPTY}
                  onChange={selectRow(row)}
                  disabled={isRowDisabled}
                />
              </td>
            )}
            {columns.map((column, colIndex) => {
              const { grow, render, rowKey, align = "left", maxWidth, colSpan } = column;
              let key: string = colIndex.toString();
              let value: ReactNode = "-";
              if (render) {
                value = render(row, rowIndex);
              } else {
                key = rowKey.toString();
                const keyValue = row[rowKey];
                value = typeof keyValue === "string" || typeof keyValue === "number" ? keyValue : "-";
              }

              const colId = `row-${rowIndex}-col-${key}`;
              const showTooltip = truncatedElementIds.includes(colId) && !render;
              const cellClassName = clsx(getCellClass(align, grow, isRowDisabled), cellClassNameExt?.(row, column));

              return (
                <td
                  key={`col-${key}`}
                  className={!showTooltip ? cellClassName : ""}
                  style={{ maxWidth }}
                  colSpan={colSpan}
                  ref={(el) => verifyOverflowingElement(el, colId)}
                >
                  {showTooltip ? (
                    <ItemWithTooltip
                      trigger={value}
                      content={row[String(rowKey)] as ReactNode}
                      cellClassName={cellClassName}
                      width={maxWidth}
                    />
                  ) : (
                    value
                  )}
                </td>
              );
            })}
          </tr>
        );
      })}
    </tbody>
  );
}
